import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { roleUtils } from './types/auth.types';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import GlobalStyles from './styles/globalStyles';
import './styles/variables.css';
import './styles/theme.css';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const PointOfSale = React.lazy(() => import('./pages/PointOfSale'));
const Products = React.lazy(() => import('./pages/Products'));
const Categories = React.lazy(() => import('./pages/Categories'));
const Brands = React.lazy(() => import('./pages/Brands'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Customers = React.lazy(() => import('./pages/Customers'));
const Inventory = React.lazy(() => import('./pages/Inventory'));
const Suppliers = React.lazy(() => import('./pages/Suppliers'));
const SupplierPurchase = React.lazy(() => import('./pages/SupplierPurchase'));
const Users = React.lazy(() => import('./pages/Users'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Profile = React.lazy(() => import('./pages/Profile'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const OrderDetails = React.lazy(() => import('./pages/OrderDetails'));
const Promotions = React.lazy(() => import('./pages/Promotions'));

// Loading component for suspense fallback
const LoadingPage = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="spinner">Loading...</div>
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

// Protected route component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles = [] }) => {
  const { auth } = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return <LoadingPage />;
  }

  if (!auth.isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for required roles if any
  if (requiredRoles.length > 0) {
    const userRoles = auth.user?.roles || [];
    const hasRequiredRole = requiredRoles.some(role => roleUtils.hasRole(userRoles, role));

    if (!hasRequiredRole) {
      // Redirect to dashboard if user doesn't have required role
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// App routes
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Dashboard />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <PointOfSale />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Products />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Categories />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/brands"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Brands />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Orders />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/promotions"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Promotions />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <OrderDetails />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Customers />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/inventory"
        element={
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MANAGER']}>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Inventory />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MANAGER']}>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Suppliers />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/suppliers/:id/purchases"
        element={
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MANAGER']}>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <SupplierPurchase />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/suppliers/:id/purchases/:purchaseId"
        element={
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MANAGER']}>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <SupplierPurchase />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/suppliers/purchases/:purchaseId"
        element={
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MANAGER']}>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <SupplierPurchase />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRoles={['ROLE_ADMIN']}>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Users />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Settings />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <React.Suspense fallback={<LoadingPage />}>
                <Profile />
              </React.Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Main App component
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <GlobalStyles />
        <React.Suspense fallback={<LoadingPage />}>
          <AppRoutes />
        </React.Suspense>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
